import { useState, useEffect } from 'react'
import {
  Container,
  Typography,
  Box,
  Stack,
  Collapse,
  Divider,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import candidates from '../data/candidates.json'

function Faq() {
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState(0)
  const [expandedItem, setExpandedItem] = useState(null)
  
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const faqItems = [
    {
      title: t('faq.kategori.klien'),
      accent: '#2563eb',
      questions: [
        { q: t('faq.pertanyaan.klien.garansi'),       a: t('faq.jawaban.klien.garansi') },
        { q: t('faq.pertanyaan.klien.wawancara'),     a: t('faq.jawaban.klien.wawancara') },
        { q: t('faq.pertanyaan.klien.antarPekerja'),  a: t('faq.jawaban.klien.antarPekerja') },
        { q: t('faq.pertanyaan.klien.memilih'),       a: t('faq.jawaban.klien.memilih', { jumlah: candidates.length }) },
      ],
    },
    {
      title: t('faq.kategori.tenagaKerja'),
      accent: '#f97316',
      questions: [
        { q: t('faq.pertanyaan.tenagaKerja.usia'),             a: t('faq.jawaban.tenagaKerja.usia') },
        { q: t('faq.pertanyaan.tenagaKerja.beratBadan'),       a: t('faq.jawaban.tenagaKerja.beratBadan') },
        { q: t('faq.pertanyaan.tenagaKerja.transportasi'),     a: t('faq.jawaban.tenagaKerja.transportasi') },
        { q: t('faq.pertanyaan.tenagaKerja.lokasiWawancara'), a: t('faq.jawaban.tenagaKerja.lokasiWawancara') },
      ],
    },
  ]

  const activeCategory = faqItems[activeTab]

  const handleTabClick = (idx) => {
    setActiveTab(idx)
    setExpandedItem(null)
  }

  const handleToggle = (idx) => {
    setExpandedItem(prev => (prev === idx ? null : idx))
  }

  return (
    <Box sx={{ bgcolor: '#f8fafc', overflow: 'hidden' }}>

      {/* ── HERO ── */}
      <Box sx={{
        position: 'relative',
        minHeight: { xs: 'auto', md: '46vh' },
        display: 'flex',
        alignItems: 'flex-end',
        background: 'linear-gradient(135deg, #060d1f 0%, #0f172a 100%)',
      }}>
        {/* Blue radial glow */}
        <Box sx={{
          position: 'absolute',
          inset: 0,
          background: 'radial-gradient(ellipse 70% 80% at 20% 50%, rgba(37,99,235,0.18), transparent 65%)',
          pointerEvents: 'none',
        }} />

        {/* Top accent bar */}
        <Box sx={{
          position: 'absolute',
          top: 0, left: 0, right: 0,
          height: 4,
          background: 'linear-gradient(90deg, #2563eb, #f97316)',
        }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, pt: { xs: 14, md: 6 }, pb: { xs: 6, md: 8 } }}>
          {/* Orange overline */}
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <Box sx={{ width: 20, height: 2, bgcolor: '#f97316', borderRadius: 1 }} />
            <Typography sx={{
              color: '#f97316',
              fontWeight: 700,
              fontSize: '0.7rem',
              textTransform: 'uppercase',
              letterSpacing: 2,
            }}>
              CV. Mulia Utama Mandiri
            </Typography>
          </Stack>

          <Typography variant="h2" sx={{
            color: '#fff',
            fontWeight: 900,
            fontSize: { xs: '2.4rem', md: '3.8rem' },
            lineHeight: 1.05,
            letterSpacing: '-0.03em',
            mb: 1.5,
          }}>
            {t('faq.header')}
          </Typography>

          <Typography sx={{
            color: 'rgba(255,255,255,0.6)',
            fontSize: '0.95rem',
            maxWidth: 520,
            lineHeight: 1.7,
          }}>
            {t('faq.subtitle')}
          </Typography>
        </Container>
      </Box>

      {/* ── CONTENT ── */}
      <Box sx={{ py: { xs: 6, md: 10 } }}>
        <Container maxWidth="lg">
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '260px 1fr' },
            gap: { xs: 4, md: 6 },
            alignItems: 'start',
          }}>

            {/* ── LEFT: Category selector ── */}
            <Box sx={{ width: '100%' }}>
              <Stack
                direction={{ xs: 'row', md: 'column' }}
                spacing={{ xs: 1, md: 1.5 }}
                sx={{ width: '100%' }}
              >
                {faqItems.map((cat, idx) => {
                  const isActive = activeTab === idx
                  return (
                    <Box
                      key={idx}
                      onClick={() => handleTabClick(idx)}
                      sx={{
                        flex: { xs: 1, md: 'none' },
                        minWidth: 0,
                        borderRadius: '14px',
                        p: { xs: 1.75, md: 2.5 },
                        cursor: 'pointer',
                        width: { xs: 'auto', md: '100%' },
                        textAlign: 'left',
                        bgcolor: isActive ? '#0f172a' : '#fff',
                        color: isActive ? '#fff' : '#475569',
                        border: isActive ? '1px solid transparent' : '1px solid #e2e8f0',
                        boxShadow: isActive
                          ? '0 8px 24px rgba(15,23,42,0.2)'
                          : '0 1px 4px rgba(0,0,0,0.04)',
                        transition: 'all 0.2s ease',
                        position: 'relative',
                        overflow: 'hidden',
                        '&:hover': {
                          borderColor: isActive ? 'transparent' : '#94a3b8',
                        },
                      }}
                    >
                      {/* Active left-bar accent */}
                      {isActive && (
                        <Box sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: 4,
                          height: '100%',
                          bgcolor: cat.accent,
                          borderRadius: '14px 0 0 14px',
                        }} />
                      )}

                      <Typography sx={{
                        fontWeight: 700,
                        fontSize: { xs: '0.78rem', md: '0.88rem' },
                        color: 'inherit',
                        pl: isActive ? 1 : 0,
                        lineHeight: 1.3,
                        transition: 'padding 0.2s',
                      }}>
                        {cat.title}
                      </Typography>

                      {/* Question count badge */}
                      <Box sx={{
                        display: 'inline-block',
                        mt: { xs: 0.75, md: 1 },
                        px: 1.2,
                        py: 0.3,
                        bgcolor: isActive ? 'rgba(255,255,255,0.12)' : '#f1f5f9',
                        color: isActive ? 'rgba(255,255,255,0.7)' : '#64748b',
                        borderRadius: '8px',
                        fontSize: { xs: '0.65rem', md: '0.7rem' },
                        fontWeight: 600,
                        pl: isActive ? 2 : 1.2,
                        transition: 'padding 0.2s',
                      }}>
                        {cat.questions.length} Pertanyaan
                      </Box>
                    </Box>
                  )
                })}
              </Stack>
            </Box>

            {/* ── RIGHT: Accordion ── */}
            <Box>
              {/* Category heading */}
              <Box sx={{ mb: 4 }}>
                <Typography sx={{
                  fontWeight: 900,
                  fontSize: '1.5rem',
                  color: '#0f172a',
                  letterSpacing: '-0.02em',
                  mb: 1.5,
                }}>
                  {activeCategory.title}
                </Typography>
                <Box sx={{
                  width: 48,
                  height: 4,
                  bgcolor: activeCategory.accent,
                  borderRadius: 2,
                }} />
              </Box>

              {/* Accordion items */}
              {activeCategory.questions.map((item, index) => {
                const isOpen = expandedItem === index
                return (
                  <Box
                    key={`${activeTab}-${index}`}
                    sx={{
                      bgcolor: '#fff',
                      borderRadius: '16px',
                      border: isOpen
                        ? `1px solid ${activeCategory.accent}44`
                        : '1px solid #e2e8f0',
                      mb: 2,
                      overflow: 'hidden',
                      boxShadow: isOpen
                        ? `0 4px 20px ${activeCategory.accent}18`
                        : '0 1px 4px rgba(0,0,0,0.04)',
                      transition: 'border-color 0.2s, box-shadow 0.2s',
                    }}
                  >
                    {/* Summary row */}
                    <Box
                      onClick={() => handleToggle(index)}
                      sx={{
                        px: 3,
                        py: 2.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        gap: 2,
                        '&:hover': {
                          bgcolor: 'rgba(0,0,0,0.015)',
                        },
                        transition: 'background 0.15s',
                      }}
                    >
                      {/* Left: accent bar + question */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}>
                        <Box sx={{
                          width: 4,
                          height: 20,
                          bgcolor: isOpen ? activeCategory.accent : '#cbd5e1',
                          borderRadius: 1,
                          flexShrink: 0,
                          transition: 'background 0.2s',
                        }} />
                        <Typography sx={{
                          fontWeight: 700,
                          color: '#0f172a',
                          fontSize: { xs: '0.9rem', md: '0.95rem' },
                          lineHeight: 1.5,
                        }}>
                          {item.q}
                        </Typography>
                      </Box>

                      {/* Right: +/- toggle */}
                      <Box sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        bgcolor: isOpen ? '#0f172a' : '#f1f5f9',
                        color: isOpen ? '#fff' : '#64748b',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        fontSize: '1.2rem',
                        fontWeight: 400,
                        lineHeight: 1,
                        transition: 'all 0.2s ease',
                        userSelect: 'none',
                      }}>
                        {isOpen ? '−' : '+'}
                      </Box>
                    </Box>

                    {/* Answer */}
                    <Collapse in={isOpen} timeout={280}>
                      <Box sx={{ px: 3, pb: 3 }}>
                        <Divider sx={{ mb: 2, borderColor: '#f1f5f9' }} />
                        <Typography sx={{
                          color: '#475569',
                          lineHeight: 1.8,
                          fontSize: { xs: '0.88rem', md: '0.93rem' },
                        }}>
                          {item.a}
                        </Typography>
                      </Box>
                    </Collapse>
                  </Box>
                )
              })}
            </Box>

          </Box>
        </Container>
      </Box>

    </Box>
  )
}

export default Faq
